'use client';

import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import { useTranslations } from 'next-intl';
import { useReducer, useState } from 'react';
import {
  applicationReducer,
  initialApplicationState,
  ApplicationStep,
  type PresetLocation,
} from './applicationReducer';
import { Step1PersonalData } from './steps/Step1PersonalData';
import { Step2LocationSelection } from './steps/Step2LocationSelection';
import { Step3Degree } from './steps/Step3Degree';
import { Step4Subjects } from './steps/Step4Subjects';
import { Step5Message } from './steps/Step5Message';
import { Step6Attachments } from './steps/Step6Attachments';
import { stepChrome, type TutorApplicationFormResolved } from './types';
import { useApplicationSubmit } from './useApplicationSubmit';
import { Section } from '@/components/Section';
import { StepIndicator } from '@/components/StepIndicator';
import type { LocationDto } from '@/lib/api';
import { generateFormCustomerId } from '@/lib/form/customerId';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { resolveCenterName } from '@/lib/location/centerName';
import { useOptionalRenderContext } from '@/lib/render/RenderContextProvider';

/** Step indicator label keys, per 10.03.04.03 AC 1. */
const STEP_LABEL_KEYS = [
  'stepPersonalData',
  'stepLocation',
  'stepDegree',
  'stepSubjects',
  'stepMessage',
  'stepAttachments',
] as const;

/**
 * The form's same-page anchor. The City-Jobs Page's "Jetzt bewerben" links
 * target it (10.03.03.27, whose `anchorTarget` defaults to this value), so it
 * sits on the submitted state too — a link must not land nowhere after a send.
 */
const APPLICATION_FORM_ANCHOR = 'bewerbungsformular';

interface Props {
  resolved: TutorApplicationFormResolved;
}

/**
 * The page's own Standort as step 2's fixed desired location (10.03.04.03
 * § AC 3), or `undefined` on a page without one. A Standort without a
 * `centerNumber` is not pre-selected: it has no NHL-Tool `schoolnumber`, so an
 * application naming it would be rejected — the applicant picks one instead.
 */
function presetLocationOf(
  location: LocationDto | undefined,
): PresetLocation | undefined {
  if (!location?.slug || location.centerNumber == null) {
    return undefined;
  }
  return {
    slug: location.slug,
    label: resolveCenterName(location),
    centerNumber: location.centerNumber,
  };
}

export const TutorApplicationFormWidget = ({ resolved }: Props) => {
  const t = useTranslations('tutorApplication');
  const preset = presetLocationOf(
    useOptionalRenderContext()?.entities.location as LocationDto | undefined,
  );
  const [state, dispatch] = useReducer(
    applicationReducer,
    preset,
    initialApplicationState,
  );

  // The Standort's center number when the page has one (Standort-Jobs Page),
  // else "0000" (the career page, the City-Jobs Page).
  const [customerNumber] = useState(() =>
    generateFormCustomerId(preset?.centerNumber),
  );

  const { handleSubmit, isPending } = useApplicationSubmit(
    dispatch,
    resolved.step6SubmitTracking,
    resolved.step6Tracking,
    customerNumber,
  );

  if (state.submitted) {
    return (
      <Section id={APPLICATION_FORM_ANCHOR}>
        <div className="rounded-card bg-white p-6">
          <BlocksRenderer content={resolved.confirmationBody} />
        </div>
      </Section>
    );
  }

  const chrome = stepChrome(resolved, state.step);
  const stepProps = {
    state,
    dispatch,
    chrome,
    onBack: () => dispatch({ type: 'STEP_BACK' as const }),
    // Each step's CTA fires its own CMS-configured GA4 event before advancing
    // (AC 10). Step 6 is different — it submits, so its tracking is split into
    // submit/success and handled by `useApplicationSubmit`.
    onNext: () => {
      pushCmsEventTracking(chrome.tracking);
      dispatch({ type: 'STEP_NEXT' as const });
    },
  };

  return (
    <Section id={APPLICATION_FORM_ANCHOR}>
      <StepIndicator
        labels={STEP_LABEL_KEYS.map((key) => t(key))}
        activeStep={state.step}
        // Progression is linear: the reducer ignores any attempt to jump
        // forward, so this only ever moves the applicant back (AC 1).
        onBack={(target) =>
          dispatch({ type: 'STEP_SET', step: target as ApplicationStep })
        }
      />

      {state.step === ApplicationStep.PersonalData && (
        <Step1PersonalData {...stepProps} />
      )}
      {state.step === ApplicationStep.Location && (
        <Step2LocationSelection {...stepProps} />
      )}
      {state.step === ApplicationStep.Degree && <Step3Degree {...stepProps} />}
      {state.step === ApplicationStep.Subjects && (
        <Step4Subjects {...stepProps} />
      )}
      {state.step === ApplicationStep.Message && (
        <Step5Message {...stepProps} />
      )}
      {state.step === ApplicationStep.Attachments && (
        <Step6Attachments
          {...stepProps}
          privacyText={resolved.step6PrivacyText}
          isSubmitting={isPending || state.submitting}
          onSubmit={() => handleSubmit(state)}
        />
      )}
    </Section>
  );
};
